import React from 'react'
import { t } from 'ttag'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  getRespondent,
  getPersonality,
  getInterest,
  getGMA,
} from 'ducks/respondent'
import { interweave } from 'helpers'
import {
  toQuiz as toQuizRoute,
  toGmaQuizWelcome,
  toInterestQuiz,
} from 'ducks/router'
import cx from 'classnames'
import styles from './PersonalityResults.module.scss'
import { BoldButton } from 'components/shared/BoldButton/BoldButton'
import PlayIcon from 'assets/icons/play.svg'
import BackIcon from 'assets/icons/back-arrow.svg'
import BackIconWhite from 'assets/icons/back-arrow-white.svg'
import { MailHint } from 'components/quiz/Quiz/Quiz'
import { hasModule, type Plan } from 'components/quiz/Quiz/plan'
import { getFactors, TAB_NAMES } from 'components/modals/TestsReportModal/model'
import { TRAITS } from 'components/common/ScoreCard/ScoreCard'
import { Accordion } from 'components/shared/Accordion/Accordion'
import { Scale } from 'components/shared/DoubleEndedScale/Scale'
import { ScrollButton } from 'components/shared/ScrollButton/ScrollButton'
import { useTooltip } from 'hooks'
import {
  TooltipContent,
  DoubleEndedScale,
} from 'components/shared/DoubleEndedScale/DoubleEndedScale'
import { TetherPortal } from 'components/shared/TetherPortal/TetherPortal'
import { FacetChip } from 'components/personality/FacetChip/FacetChip'

import {
  getTraitsTranslations,
  getTraitTranslation,
} from 'components/modals/TestsReportModal/tabs/helpers'

const Trait = ({ label, scores, abstract, low, mid, high, id }) => {
  const {
    state: {
      arrowRef,
      arrowStyle,
      floating,
      floatingStyle,
      reference,
      hasArrow,
      isToggled,
    },
    handlers: {
      handleBlur,
      handleFocus,
      handleMouseEnter,
      handleMouseLeave,
      handleTouchEnd,
      handleTouchStart,
    },
  } = useTooltip({ initialPlacement: 'left-end' })

  const score = scores[0].score

  return (
    <>
      <div
        key={label}
        id={id}
        {...{
          ref: reference,
          onBlur: handleBlur,
          onFocus: handleFocus,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          onTouchStart: handleTouchStart,
          onTouchEnd: handleTouchEnd,
        }}
      >
        <FacetChip label={label} score={score} />
      </div>

      <TetherPortal isOpen={isToggled}>
        <TooltipContent
          {...{
            score,
            hasArrow,
            floating,
            floatingStyle,
            arrowRef,
            arrowStyle,
            label,
            abstract,
            low,
            mid,
            high,
          }}
        />
      </TetherPortal>
    </>
  )
}

type PersonalityResultsProps = {
  scrollPosition?: { scrollTop: number }
  handleScrollToTop?: () => void
}

type PersonalityResultsState = {
  id: string
  scores?: any
  language?: string
  gmaStatus?: string
  interestStatus?: string
  is_job_seeker?: boolean
  plan?: Plan
}

const selectResults = (state): PersonalityResultsState => {
  const gmaState = getGMA(state)
  const interestState = getInterest(state)

  return {
    ...getPersonality(state),
    ...getRespondent(state),
    gmaStatus: gmaState.status,
    interestStatus: interestState.status,
  }
}

const PersonalityResults = ({
  scrollPosition,
  handleScrollToTop,
}: PersonalityResultsProps) => {
  const {
    id: uuid,
    scores,
    language,
    gmaStatus,
    interestStatus,
    is_job_seeker,
    plan,
  } = useSelector(selectResults, shallowEqual)
  const dispatch = useDispatch()
  const toQuiz = uuid => dispatch(toQuizRoute({ uuid }))
  const toGma = uuid => dispatch(toGmaQuizWelcome({ uuid }))
  const toInterest = uuid => dispatch(toInterestQuiz({ uuid }))

  const factors = getFactors(true, is_job_seeker).filter(
    factor => factor?.parentId === TAB_NAMES.PERSONALITY,
  )

  const [selectedFactor, setSelectedFactor] = React.useState(factors[0].factor)

  const gmaIsNext = hasModule(plan, 'gma') && gmaStatus !== 'completed'
  const isInterestCompleted = interestStatus === 'completed'

  const traits = factors.map(({ factor, label, id }) => ({
    id,
    label,
    factor,
    score: Math.round(scores[factor] * 100),
  }))

  const attributes = getTraitsTranslations(
    traits.map(trait => trait.factor),
    [{ id: uuid, scores: scores }],
    language,
    true,
  )

  const getTraitDescription = attributeId => {
    const {
      label,
      abstract = [],
      description = [],
    } = getTraitTranslation(attributeId, scores[attributeId], language, true)

    const subTraits = getTraitsTranslations(
      TRAITS[attributeId],
      [{ id: uuid, scores: scores }],
      language,
    )

    const trait = label[0]

    return (
      <div className={styles.description}>
        <div className={styles.title}>{trait}</div>

        {abstract.map((par, index) => (
          <div key={index} className={styles.paragraph}>
            {par}
          </div>
        ))}

        <div className={cx(styles.paragraph, styles.attributeDescription)}>
          {interweave(description, ' ')}
        </div>
        <div className={styles.facetsTitle}>{t`Facets of ${trait}`}</div>
        <div className={styles.subtitle}>
          {t`For full description of facets, we recommend opening the report on desktop.`}
        </div>
        <div className={styles.facets}>
          {subTraits.map(trait => (
            <Trait key={trait.id} {...trait} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.personalityResults}>
      <ScrollButton {...{ handleScrollToTop, scrollPosition }} />
      <div className={styles.title}>{t`Personality test results`}</div>
      <div className={styles.content}>
        <div className={styles.attributes}>
          {attributes.map(attribute => (
            <DoubleEndedScale
              {...attribute}
              single
              key={attribute.label}
              initialPlacement={'left-start'}
              selectable
              isColoredBackground
              selected={attribute.id === selectedFactor}
              onClick={() => setSelectedFactor(attribute.id)}
            />
          ))}
        </div>
        {getTraitDescription(selectedFactor)}
      </div>

      <div className={styles.mobileContainer}>
        {attributes.map((attribute, index) => (
          <Accordion key={attribute.label} className={styles.accordeon}>
            <Accordion.Header
              className={styles.accordeonHeader}
              title={
                <span className={styles.headerTitle}>
                  {attribute.label}
                  <span className={styles.headerScore}>
                    {attribute.scores[0]?.score}
                  </span>
                </span>
              }
            />
            <Accordion.Panel className={styles.accordeonPanel}>
              <div className={styles.panelContent}>
                <div className={styles.scale}>
                  <Scale
                    scores={[{ id: uuid, score: attribute?.scores[0]?.score }]}
                    single
                    withHighlight={false}
                  />
                  <div className={styles.scaleLabel}>
                    <span>{attribute.lowLabel}</span>
                    <span>{attribute.highLabel}</span>
                  </div>
                </div>

                {getTraitDescription(attribute.id)}
              </div>
            </Accordion.Panel>
          </Accordion>
        ))}
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.buttons}>
          <BoldButton
            label={t`Back to intro page`}
            icon={gmaIsNext ? BackIcon : BackIconWhite}
            isIconFirst
            isPrimary={!gmaIsNext}
            onClick={() => toQuiz(uuid)}
          />
          {gmaIsNext ? (
            <BoldButton
              label={t`Go to ability test`}
              icon={PlayIcon}
              isIconFirst
              onClick={() => toGma(uuid)}
            />
          ) : is_job_seeker && !isInterestCompleted ? (
            <BoldButton
              label={t`Go to interest test`}
              icon={PlayIcon}
              onClick={() => toInterest(uuid)}
            />
          ) : null}
        </div>
        <MailHint />
      </div>
    </div>
  )
}

export default PersonalityResults
